import { useState, useMemo, useEffect } from "react";
import { ChevronLeft, ChevronRight, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { InternalComboWithDetails } from "@/hooks/useInternalCombos";

interface GarcomComboGridProps {
  combos: InternalComboWithDetails[];
  getComboCartCount: (comboId: string) => number;
  onComboClick: (combo: InternalComboWithDetails) => void;
  formatCurrency: (value: number) => string;
}

const ITEMS_PER_PAGE = 12;

export function GarcomComboGrid({
  combos,
  getComboCartCount,
  onComboClick,
  formatCurrency,
}: GarcomComboGridProps) {
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [combos.length]);

  const totalPages = Math.ceil(combos.length / ITEMS_PER_PAGE);

  const paginatedCombos = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return combos.slice(start, start + ITEMS_PER_PAGE);
  }, [combos, currentPage]);

  const handlePreviousPage = () => {
    setCurrentPage((p) => Math.max(1, p - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((p) => Math.min(totalPages, p + 1));
  };

  if (combos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 text-center">
        <Package className="h-12 w-12 text-muted-foreground/30 mb-2" />
        <p className="text-muted-foreground">Nenhum combo disponível</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 grid grid-cols-2 gap-1.5 content-start">
        {paginatedCombos.map((combo) => {
          const qty = getComboCartCount(combo.id);
          return (
            <Card
              key={combo.id}
              className={cn(
                "p-2 relative overflow-hidden transition-all cursor-pointer",
                qty > 0 && "ring-2 ring-primary"
              )}
              onClick={() => onComboClick(combo)}
            >
              <div className="w-full text-left">
                <div className="flex items-center gap-1 mb-0.5">
                  <Package className="h-3 w-3 text-muted-foreground" />
                  <Badge variant="secondary" className="text-[9px] h-4 px-1">
                    Combo
                  </Badge>
                </div>
                <p className="font-medium text-xs line-clamp-2 mb-0.5">
                  {combo.name}
                </p>
                <p className="text-xs font-bold text-primary">
                  {formatCurrency(Number(combo.price))}
                </p>
              </div>

              {qty > 0 && (
                <Badge className="absolute top-1 right-1 h-5 w-5 p-0 flex items-center justify-center">
                  {qty}
                </Badge>
              )}
            </Card>
          );
        })}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 py-2 border-t mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="h-8 px-2"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="h-8 px-2"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
